import React, { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import useApiList from "@/hooks/useApiList";
import CourseCard from "@/components/CourseCard";
import { Search, Loader2, AlertCircle } from "lucide-react";

const CATEGORIES = ["All", "Entry Level", "Professional", "Leadership", "Executive", "Postgraduate"];

export default function CoursesPage() {
  useSeo({ title: "Programmes & Examinations", description: "Browse CSI programmes and examinations - CSCE, CSP, CSL, CCSO and PGDCSL - delivered live with native online assessment.", path: "/courses" });
  const { data: courses, loading, error, refetch } = useApiList("/courses");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      if (cat !== "All" && c.category !== cat) return false;
      if (q && !c.title.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [courses, cat, q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col gap-4 mb-8">
        <div className="eyebrow">Catalog</div>
        <h1 className="font-display text-4xl sm:text-5xl">Find your next cohort</h1>
        <p className="text-zinc-600 max-w-2xl">Pick a track, filter by category, and pre-register for live sessions. New courses added every month.</p>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses…"
            className="w-full h-11 rounded-full border border-[var(--border-warm)] bg-white pl-10 pr-4 text-sm outline-none focus:border-[var(--ink)]"
            data-testid="courses-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`chip ${cat === c ? "chip-active" : ""}`}
              data-testid={`category-chip-${c}`}
            >{c}</button>
          ))}
        </div>
      </div>

      {loading && courses.length === 0 ? (
        <div className="py-20 text-center text-zinc-500 inline-flex items-center justify-center gap-2 w-full" data-testid="courses-loading">
          <Loader2 size={16} className="animate-spin"/> Loading the programme catalogue…
        </div>
      ) : error && courses.length === 0 ? (
        <div className="py-20 text-center" data-testid="courses-error">
          <AlertCircle size={28} className="mx-auto text-[var(--terracotta)] mb-2"/>
          <div className="text-sm text-zinc-700 mb-3">We couldn't load the catalogue right now.</div>
          <button className="chip inline-flex items-center gap-1" onClick={refetch} data-testid="courses-retry">Try again</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500">No courses match your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
          {filtered.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      )}
    </div>
  );
}
